import { Injectable } from '@angular/core';
import { ApiService } from '../../services/apiservice/apiservice.service';
import { StorageService } from '../../services/storage/storage.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConfigService } from '../../services/config/config.service';
import { HttpClient } from '@angular/common/http';
import {
  IGetPopularListsResponse,
  IListHoldInfo,
  IListInfo,
  IListItem,
  IQuickStatus,
  IShareList,
} from '../../core/interfaces/list-interface';
import { filter, map } from 'rxjs/operators';
import { LOGIN_STATE, SharedService } from '../../services/shared/shared.service';
import { LayoutService } from '../../services/layout.service';

@Injectable()
export class YourListService {
  private _lists = new BehaviorSubject<any>(null);

  lists$ = this._lists.asObservable();

  constructor(
    private _http: HttpClient,
    private _apiService: ApiService,
    private _configService: ConfigService,
    private _layoutService: LayoutService,
    private _sharedService: SharedService,
    private _storageService: StorageService,
  ) {
    this._sharedService
      .getLoginStatusBS()
      .pipe(filter((status) => status == LOGIN_STATE.LOGOUT))
      .subscribe(() => {
        this._lists.next(null);
      });
  }

  getShowcaseList(id): Observable<IListInfo> {
    const url = `${this._configService.agApi}/ShowCase/list`;
    return this._apiService.post(url, id).pipe(
      map((response) => {
        if (response?.data) {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  getShowcasePopularList(listId: number): Observable<IListInfo> {
    const url = `${this._configService.agApi}/ShowCase/popularlist/${listId}`;
    return this._apiService.get(url).pipe(
      map((response) => {
        if (response?.data) {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  getPopularLists(): Observable<IGetPopularListsResponse> {
    const url = `${this._configService.agApi}/List/GetPopularLists`;
    return this._apiService.get(url).pipe(
      map((response) => {
        if (response?.data) {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  getListInfo(listId: number): Observable<IListInfo> {
    const url = `${this._configService.agApi}/List/GetListInfo?listId=${listId}`;
    return this._apiService.get(url).pipe(
      map((response) => {
        if (response?.data) {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  deleteList(listId: number): Observable<IQuickStatus> {
    const data = { listId: listId };
    const url = `${this._configService.agApi}/List/ListDelete`;
    return this._apiService.post(url, data).pipe(
      map((response) => {
        if (response?.data) {
          this.loadLists().subscribe();
          return response.data;
        } else {
          throw Error('Unable to delete list');
        }
      }),
    );
  }

  createList(name: string): Observable<any> {
    const data = { newListName: name };
    const url = this._configService.agApi + '/List/ListCreate';
    return this._apiService.post(url, data).pipe(
      map((response) => {
        if (response?.data.success) {
          this.loadLists().subscribe();
          return parseInt(response.data.success);
        } else {
          throw Error('Unable to create list.');
        }
      }),
    );
  }

  loadLists(): Observable<any> {
    const url = this._configService.agApi + '/List/GetLists';
    return this._apiService.get(url).pipe(
      map((res) => {
        this._lists.next(res?.data);
        return res.data;
      }),
    );
  }

  addListItem(agControlId: string, source: string, listId: number = 0): Observable<any> {
    const data = {
      agcontrolid: agControlId,
      source: source,
      listId: listId, // 0 for guest
    };
    const url = this._configService.agApi + '/List/ListAddItem';
    return this._apiService.post(url, data).pipe(
      map((response) => {
        if (response?.data) {
          this.loadLists().subscribe();
          this._sharedService.setLoadGuestListBS(true);
          this._layoutService.openSideNav({
            template: 'addList',
            position: 'end',
            data: data,
          });
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  addItemToListFromSource(
    agControlId: string,
    source: string,
    listId: number = 0,
  ): Observable<string> {
    return this.addListItem(agControlId, source, listId).pipe(
      map((response) => {
        this.loadLists().subscribe();
        this._sharedService.openSnackBar(response.msg, 'ok');
        if (response.success) {
          return response.msg;
        }
      }),
    );
  }

  addItemToListFromLibKey(
    agControlId: string,
    libraryDbPoolKey: number,
    listId: number = 0,
  ): Observable<any> {
    const data = {
      agControlId: agControlId,
      libraryDbPoolKey: libraryDbPoolKey,
      listId: listId,
    };
    const url = this._configService.agApi + '/list/ListAddItemByAgcn';
    return this._apiService.post(url, data).pipe(
      map((response) => {
        this.loadLists().subscribe();
        return response;
      }),
    );
  }

  deleteListItem(listId: number, listItemIds: number[]): Observable<void> {
    const url = this._configService.agApi + '/List/ListDeleteItem';
    const data = {
      listId: listId,
      items: listItemIds,
    };
    return this._apiService.post(url, data).pipe(
      map((response) => {
        if (response?.data) {
          this.loadLists().subscribe();
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  moveListItems(toListId: number, listItemIds: number[]): Observable<any> {
    const url = this._configService.agApi + '/List/ListMoveItem';
    const data = {
      listId: toListId,
      items: listItemIds,
    };
    return this._apiService.post(url, data).pipe(
      map((response) => {
        if (response?.data) {
          this.loadLists().subscribe();
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  printList(listId: number, items: number[]): Observable<any> {
    const data = {
      listId: listId,
      items: items,
    };
    const url = this._configService.agApi + '/List/PrintList';
    return this._apiService.post(url, data);
  }

  downloadListItems(data, filename: string = 'response.txt'): Observable<any> {
    const url = this._configService.agApi + '/List/SaveList';
    return this._apiService.postWithTokenFileDownload(url, data).pipe(
      map((response) => {
        return { filename: filename, data: response };
      }),
    );
  }

  updateListName(data): Observable<any> {
    const url = this._configService.agApi + '/List/ListUpdateName';
    return this._apiService.post(url, data).pipe(
      map((data) => {
        this.loadLists().subscribe();
        return data;
      }),
    );
  }

  downloadList(data): Observable<any> {
    const url = this._configService.agApi + '/List/Download';
    return this._apiService.postWithTokenFileDownload(url, data);
  }

  setShareListStatus(data): Observable<any> {
    const url = `${this._configService.agApi}/List/UpdateListStatus`;
    return this._http.patch<any>(url, data);
  }

  shareList(data: IShareList): Observable<any> {
    const url = `${this._configService.agApi}/list/sharelist`;
    return this._apiService.post(url, data);
  }

  // #region hold multiple items your list
  setItemsArray(value): void {
    this._storageService.setItem('itemsArrayMUL', value);
  }

  setHoldInfo(value): void {
    this._storageService.setItem('holdInfo', value);
  }

  setListId(value): void {
    this._storageService.setItem('list-id', value);
  }

  getItemsArray(): IListItem[] {
    return this._storageService.getItem('itemsArrayMUL');
  }

  getHoldInfo(): IListHoldInfo {
    return this._storageService.getItem('holdInfo');
  }

  getListId(): number {
    return this._storageService.getItem('list-id');
  }

  // #endregion
}
